import { encode } from "uqr";
import { contrastRatio, opaqueHex, parseHex, relativeLuminance } from "./color";
import type { QrEcc, QrElement, QrLogo, QrStyle } from "./schema";

/**
 * QR code elements (docs/04-dokumentformat.md). The modules come from `uqr`
 * (also used for the 2FA setup), the renderer draws them as one SVG path, so
 * print PDFs stay vector. Content types (link, e-mail, Wi-Fi …) are only a
 * way to write `value`; `parseQrContent` reads them back for the editor.
 */

export interface QrMatrix {
  /** Modules per side, without the quiet zone. */
  size: number;
  version: number;
  /** Row-major, true = dark module. */
  dark: boolean[][];
  /** Finder patterns (the three big corner squares), drawn separately. */
  finder: boolean[][];
}

const cache = new Map<string, QrMatrix | null>();
const CACHE_LIMIT = 200;

/** Position type of uqr (`QrCodeDataType.Position`). */
const POSITION = 2;

/** The module matrix, or null when the text does not fit into a QR code. */
export function qrMatrix(value: string, ecc: QrEcc): QrMatrix | null {
  const key = `${ecc}\u0000${value}`;
  const cached = cache.get(key);
  if (cached !== undefined) return cached;
  let matrix: QrMatrix | null = null;
  try {
    const result = encode(value, { ecc, border: 0 });
    matrix = {
      size: result.size,
      version: result.version,
      dark: result.data,
      finder: result.types.map((row) => row.map((type) => type === POSITION)),
    };
  } catch {
    matrix = null;
  }
  if (cache.size >= CACHE_LIMIT) cache.delete(cache.keys().next().value!);
  cache.set(key, matrix);
  return matrix;
}

const f = (value: number) => String(Math.round(value * 1000) / 1000);

/** Square of modules (module units, without quiet zone) that a logo covers. */
export interface QrLogoArea {
  /** First covered module in both directions. */
  start: number;
  /** Covered modules per side (odd, so the area sits exactly in the middle). */
  count: number;
}

/**
 * The modules under a logo: an odd number of whole modules around the centre,
 * never reaching the finder patterns (7 modules + 1 separator in the corners).
 */
export function qrLogoArea(size: number, logo: Pick<QrLogo, "size">): QrLogoArea {
  // Sizes are odd (21, 25, …), so this is odd as well.
  const largest = Math.max(1, size - 16);
  let count = Math.round(size * logo.size);
  if (count % 2 === 0) count += 1;
  count = Math.max(1, Math.min(largest, count));
  return { start: (size - count) / 2, count };
}

/** Room between cleared modules and the logo, in modules. */
export const QR_LOGO_PADDING = 0.5;

/**
 * Where the logo is drawn, in module units of the whole code (quiet zone
 * included, like `qrModulesPath` with `offset`).
 */
export function qrLogoRect(
  matrix: QrMatrix,
  logo: Pick<QrLogo, "size" | "clear">,
  offset: number,
): { x: number; y: number; size: number } {
  const area = qrLogoArea(matrix.size, logo);
  const pad = logo.clear ? QR_LOGO_PADDING : 0;
  return { x: offset + area.start + pad, y: offset + area.start + pad, size: area.count - 2 * pad };
}

/**
 * SVG path data of the dark modules in module units (the code starts at
 * `offset`, one module = 1), to be filled with `fill-rule="evenodd"`.
 * Squares are merged into horizontal runs, so there are no hairline gaps
 * between neighbouring modules.
 */
export function qrModulesPath(
  matrix: QrMatrix,
  style: QrStyle,
  offset: number,
  /** Modules left out (the area under a logo). */
  skip?: QrLogoArea | null,
): string {
  const parts: string[] = [];
  const { size, dark, finder } = matrix;
  const skipped = (x: number, y: number) =>
    skip != null &&
    x >= skip.start &&
    x < skip.start + skip.count &&
    y >= skip.start &&
    y < skip.start + skip.count;
  const drawn = (x: number, y: number) => dark[y]![x] && !finder[y]![x] && !skipped(x, y);
  for (let y = 0; y < size; y++) {
    let x = 0;
    while (x < size) {
      if (!drawn(x, y)) {
        x++;
        continue;
      }
      if (style === "square") {
        let end = x;
        while (end < size && drawn(end, y)) end++;
        parts.push(`M${f(offset + x)} ${f(offset + y)}h${end - x}v1h${-(end - x)}z`);
        x = end;
        continue;
      }
      const cx = offset + x + 0.5;
      const cy = offset + y + 0.5;
      if (style === "dots") {
        const r = 0.42;
        parts.push(
          `M${f(cx - r)} ${f(cy)}a${r} ${r} 0 1 0 ${f(2 * r)} 0a${r} ${r} 0 1 0 ${f(-2 * r)} 0z`,
        );
      } else {
        // Rounded squares that touch their neighbours (reads like soft blobs).
        const r = 0.3;
        const s = 1;
        const x0 = offset + x;
        const y0 = offset + y;
        parts.push(
          `M${f(x0 + r)} ${f(y0)}h${f(s - 2 * r)}a${r} ${r} 0 0 1 ${r} ${r}v${f(s - 2 * r)}a${r} ${r} 0 0 1 ${-r} ${r}h${f(-(s - 2 * r))}a${r} ${r} 0 0 1 ${-r} ${-r}v${f(-(s - 2 * r))}a${r} ${r} 0 0 1 ${r} ${-r}z`,
        );
      }
      x++;
    }
  }
  // Finder patterns: 7×7 ring + 3×3 centre in each of the three corners.
  const corners = [
    [0, 0],
    [size - 7, 0],
    [0, size - 7],
  ] as const;
  const round = style === "square" ? 0 : style === "rounded" ? 1.6 : 2.2;
  const rect = (x: number, y: number, w: number, h: number, r: number, reverse = false) => {
    if (r <= 0) {
      return reverse ? `M${f(x)} ${f(y)}v${h}h${w}v${-h}z` : `M${f(x)} ${f(y)}h${w}v${h}h${-w}z`;
    }
    const q = Math.min(r, w / 2, h / 2);
    if (reverse) {
      return `M${f(x + q)} ${f(y)}a${f(q)} ${f(q)} 0 0 0 ${f(-q)} ${f(q)}v${f(h - 2 * q)}a${f(q)} ${f(q)} 0 0 0 ${f(q)} ${f(q)}h${f(w - 2 * q)}a${f(q)} ${f(q)} 0 0 0 ${f(q)} ${f(-q)}v${f(-(h - 2 * q))}a${f(q)} ${f(q)} 0 0 0 ${f(-q)} ${f(-q)}z`;
    }
    return `M${f(x + q)} ${f(y)}h${f(w - 2 * q)}a${f(q)} ${f(q)} 0 0 1 ${f(q)} ${f(q)}v${f(h - 2 * q)}a${f(q)} ${f(q)} 0 0 1 ${f(-q)} ${f(q)}h${f(-(w - 2 * q))}a${f(q)} ${f(q)} 0 0 1 ${f(-q)} ${f(-q)}v${f(-(h - 2 * q))}a${f(q)} ${f(q)} 0 0 1 ${f(q)} ${f(-q)}z`;
  };
  for (const [cx, cy] of corners) {
    const x = offset + cx;
    const y = offset + cy;
    // Outer ring with a hole (even-odd), the centre square inside it.
    parts.push(rect(x, y, 7, 7, round));
    parts.push(rect(x + 1, y + 1, 5, 5, Math.max(0, round - 0.8), true));
    parts.push(rect(x + 2, y + 2, 3, 3, round > 0 ? round * 0.6 : 0));
  }
  return parts.join("");
}

// ---------------------------------------------------------------------------
// Content types
// ---------------------------------------------------------------------------

export const QR_CONTENT_KINDS = ["url", "text", "email", "phone", "wifi", "vcard"] as const;
export type QrContentKind = (typeof QR_CONTENT_KINDS)[number];

export const QR_CONTENT_LABELS: Record<QrContentKind, string> = {
  url: "Link",
  text: "Text",
  email: "E-Mail",
  phone: "Telefon",
  wifi: "WLAN",
  vcard: "Kontakt",
};

export type QrContent =
  | { kind: "url"; url: string }
  | { kind: "text"; text: string }
  | { kind: "email"; email: string; subject: string }
  | { kind: "phone"; phone: string }
  | {
      kind: "wifi";
      ssid: string;
      password: string;
      security: "WPA" | "WEP" | "nopass";
      hidden: boolean;
    }
  | {
      kind: "vcard";
      name: string;
      organization: string;
      phone: string;
      email: string;
      url: string;
      address: string;
    };

export function emptyQrContent(kind: QrContentKind): QrContent {
  switch (kind) {
    case "url":
      return { kind, url: "https://" };
    case "text":
      return { kind, text: "" };
    case "email":
      return { kind, email: "", subject: "" };
    case "phone":
      return { kind, phone: "" };
    case "wifi":
      return { kind, ssid: "", password: "", security: "WPA", hidden: false };
    case "vcard":
      return { kind, name: "", organization: "", phone: "", email: "", url: "", address: "" };
  }
}

/** Wi-Fi / MECARD escaping: \ ; , : " */
const escapeWifi = (value: string) => value.replace(/([\\;,:"])/g, "\\$1");
const unescapeWifi = (value: string) => value.replace(/\\(.)/g, "$1");
/** vCard 3.0 escaping. */
const escapeVcard = (value: string) =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/([;,])/g, "\\$1");
const unescapeVcard = (value: string) => value.replace(/\\n/gi, "\n").replace(/\\([\\;,])/g, "$1");

/** The text a QR code encodes for some content. */
export function qrValue(content: QrContent): string {
  switch (content.kind) {
    case "url":
      return content.url.trim();
    case "text":
      return content.text;
    case "email": {
      const email = content.email.trim();
      const subject = content.subject.trim();
      return subject ? `mailto:${email}?subject=${encodeURIComponent(subject)}` : `mailto:${email}`;
    }
    case "phone":
      return `tel:${content.phone.replace(/[^\d+*#]/g, "")}`;
    case "wifi": {
      const password = content.security === "nopass" ? "" : `P:${escapeWifi(content.password)};`;
      return `WIFI:T:${content.security};S:${escapeWifi(content.ssid)};${password}${content.hidden ? "H:true;" : ""};`;
    }
    case "vcard": {
      const lines = ["BEGIN:VCARD", "VERSION:3.0"];
      const name = content.name.trim();
      const parts = name.split(/\s+/).filter(Boolean);
      const last = parts.length > 1 ? parts[parts.length - 1]! : name;
      const first = parts.length > 1 ? parts.slice(0, -1).join(" ") : "";
      lines.push(`N:${escapeVcard(last)};${escapeVcard(first)};;;`);
      lines.push(`FN:${escapeVcard(name)}`);
      if (content.organization.trim())
        lines.push(`ORG:${escapeVcard(content.organization.trim())}`);
      if (content.phone.trim()) lines.push(`TEL;TYPE=CELL:${content.phone.trim()}`);
      if (content.email.trim()) lines.push(`EMAIL:${content.email.trim()}`);
      if (content.url.trim()) lines.push(`URL:${content.url.trim()}`);
      if (content.address.trim()) lines.push(`ADR:;;${escapeVcard(content.address.trim())};;;;`);
      lines.push("END:VCARD");
      return lines.join("\n");
    }
  }
}

function splitFields(body: string): Map<string, string> {
  const fields = new Map<string, string>();
  let key = "";
  let value = "";
  let inKey = true;
  for (let index = 0; index < body.length; index++) {
    const char = body[index]!;
    if (char === "\\" && index + 1 < body.length) {
      if (inKey) key += body[index + 1];
      else value += char + body[index + 1];
      index++;
      continue;
    }
    if (inKey && char === ":") {
      inKey = false;
      continue;
    }
    if (!inKey && char === ";") {
      if (key) fields.set(key.toUpperCase(), value);
      key = "";
      value = "";
      inKey = true;
      continue;
    }
    if (inKey) key += char;
    else value += char;
  }
  if (key && !inKey) fields.set(key.toUpperCase(), value);
  return fields;
}

/** Reads the content type back from an encoded text (unknown text → plain text / link). */
export function parseQrContent(value: string): QrContent {
  const text = value.trim();
  if (/^mailto:/i.test(text)) {
    const [address, query = ""] = text.slice(7).split("?");
    const param = query.split("&").find((part) => /^subject=/i.test(part));
    let subject = "";
    try {
      subject = param ? decodeURIComponent(param.slice(8).replace(/\+/g, " ")) : "";
    } catch {
      subject = param?.slice(8) ?? "";
    }
    let email = address ?? "";
    try {
      email = decodeURIComponent(email);
    } catch {
      // Keep the raw address.
    }
    return { kind: "email", email, subject };
  }
  if (/^tel:/i.test(text)) return { kind: "phone", phone: text.slice(4) };
  if (/^WIFI:/i.test(text)) {
    const fields = splitFields(text.slice(5));
    const type = (fields.get("T") ?? "WPA").toUpperCase();
    return {
      kind: "wifi",
      ssid: unescapeWifi(fields.get("S") ?? ""),
      password: unescapeWifi(fields.get("P") ?? ""),
      security: type === "WEP" ? "WEP" : type === "NOPASS" || type === "" ? "nopass" : "WPA",
      hidden: (fields.get("H") ?? "").toLowerCase() === "true",
    };
  }
  if (/^BEGIN:VCARD/i.test(text)) {
    const content = emptyQrContent("vcard") as Extract<QrContent, { kind: "vcard" }>;
    for (const line of text.split(/\r?\n/)) {
      const colon = line.indexOf(":");
      if (colon < 0) continue;
      const name = line.slice(0, colon).split(";")[0]!.toUpperCase();
      const data = line.slice(colon + 1);
      if (name === "FN") content.name = unescapeVcard(data);
      else if (name === "ORG") content.organization = unescapeVcard(data);
      else if (name === "TEL") content.phone = data;
      else if (name === "EMAIL") content.email = data;
      else if (name === "URL") content.url = data;
      else if (name === "ADR") content.address = unescapeVcard(data.split(";")[2] ?? "");
    }
    return content;
  }
  if (/^(https?:\/\/|www\.)\S+$/i.test(text)) return { kind: "url", url: text };
  return { kind: "text", text: value };
}

/** Short description of the content (layer names, search). */
export function qrSummary(value: string): string {
  const content = parseQrContent(value);
  switch (content.kind) {
    case "url":
      return content.url.replace(/^https?:\/\//i, "").replace(/\/$/, "");
    case "text":
      return content.text.split("\n")[0] ?? "";
    case "email":
      return content.email;
    case "phone":
      return content.phone;
    case "wifi":
      return `WLAN ${content.ssid}`;
    case "vcard":
      return content.name || content.organization;
  }
}

export type QrProblem = "too-long" | "contrast" | "inverted" | "quiet-zone" | "logo-large";

/**
 * Share of modules a logo may cover per correction level. The levels restore
 * 7/15/25/30 % of the code words; scans of the covered codes (test/qr.test.ts,
 * jsQR) fail at a little more than half of that, so we stay below.
 */
const QR_LOGO_COVERAGE: Record<QrEcc, number> = { L: 0.028, M: 0.06, Q: 0.1, H: 0.12 };

/** Correction level a code with a logo should at least have. */
export const QR_LOGO_ECC: QrEcc = "H";

/** Share of the modules a logo covers (0 without logo or matrix). */
export function qrLogoCoverage(matrix: QrMatrix, logo: Pick<QrLogo, "size">): number {
  const area = qrLogoArea(matrix.size, logo);
  return (area.count * area.count) / (matrix.size * matrix.size);
}

/** Problems that make a QR code hard to scan (editor hints, print preflight). */
export function qrProblems(
  element: Pick<QrElement, "value" | "ecc" | "color" | "background" | "margin" | "logo">,
  /** Colour behind a transparent QR background (usually the page). */
  behind = "#ffffff",
): QrProblem[] {
  const problems: QrProblem[] = [];
  const matrix = qrMatrix(element.value, element.ecc);
  if (!matrix) problems.push("too-long");
  else if (element.logo && qrLogoCoverage(matrix, element.logo) > QR_LOGO_COVERAGE[element.ecc])
    problems.push("logo-large");
  const background = parseHex(element.background).a < 0.5 ? behind : element.background;
  const modules = opaqueHex(element.color);
  const back = opaqueHex(background);
  if (contrastRatio(modules, back) < 3) problems.push("contrast");
  // Light modules on a dark background: many (older) scanner apps fail.
  else if (relativeLuminance(modules) > relativeLuminance(back)) problems.push("inverted");
  if (element.margin < 1) problems.push("quiet-zone");
  return problems;
}

export const QR_PROBLEM_LABELS: Record<QrProblem, string> = {
  "too-long": "Der Inhalt ist zu lang für einen QR-Code.",
  contrast: "Zu wenig Kontrast zwischen Code und Hintergrund – Handys lesen ihn schlecht.",
  inverted: "Heller Code auf dunklem Grund: manche Kamera-Apps erkennen das nicht.",
  "quiet-zone": "Ohne Rand (Ruhezone) erkennen Kameras den Code schlechter.",
  "logo-large":
    "Das Logo verdeckt zu viel vom Code – Logo verkleinern oder die Fehlerkorrektur erhöhen.",
};
